import { CarreraItem } from "../types";
import { isTokenExpired } from "./authHelpers";

export const SESSION_SNAPSHOT_KEY = "sessionSnapshot";

export interface SessionSnapshot {
  token: string;
  id: number;
  email: string;
  completeName: { name: string; lastName: string };
  studentInitState: number;
  listadoCarreras: CarreraItem[];
  carreraActual: string;
  mostrarHorarios: boolean;
  mostrarAulas?: boolean;
  bloqueoActivo: boolean;
  bloqueoMensaje: string;
  bloqueoSubmensaje: string;
  debeAlertarSuperposicion: boolean;
  debeFiltrarCursosConCupoNoAsignado: boolean;
  debeConsultarCupos?: boolean;
}

export function isValidSessionSnapshot(value: unknown): value is SessionSnapshot {
  if (!value || typeof value !== "object") return false;
  const s = value as Partial<SessionSnapshot>;
  return (
    typeof s.token === "string" &&
    s.token.length > 5 &&
    typeof s.id === "number" &&
    s.id > 0 &&
    typeof s.email === "string" &&
    s.email.length > 0 &&
    Array.isArray(s.listadoCarreras) &&
    typeof s.studentInitState === "number"
  );
}

export function loadSessionSnapshot(): SessionSnapshot | null {
  try {
    const raw = localStorage.getItem(SESSION_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isValidSessionSnapshot(parsed)) {
      clearSessionSnapshot();
      return null;
    }
    return parsed;
  } catch {
    clearSessionSnapshot();
    return null;
  }
}

/**
 * Returns a snapshot usable for hydration when it matches the current token
 * and the token is not expired.
 */
export function getHydratableSessionSnapshot(token: string): SessionSnapshot | null {
  if (!token || token.length <= 5) return null;
  if (isTokenExpired(token)) return null;

  const snapshot = loadSessionSnapshot();
  if (!snapshot) return null;
  if (snapshot.token !== token) return null;
  if (isTokenExpired(snapshot.token)) return null;

  return snapshot;
}

export function saveSessionSnapshot(snapshot: SessionSnapshot): void {
  if (!isValidSessionSnapshot(snapshot)) return;
  localStorage.setItem(SESSION_SNAPSHOT_KEY, JSON.stringify(snapshot));
}

export function clearSessionSnapshot(): void {
  localStorage.removeItem(SESSION_SNAPSHOT_KEY);
}

/** Builds and persists a snapshot from student-like fields. */
export function persistSessionFromStudent(fields: {
  token: string;
  id: number;
  email: string;
  completeName?: { name: string; lastName: string };
  studentInitState: number;
  listadoCarreras: CarreraItem[] | unknown[];
  carreraActual?: string;
  mostrarHorarios?: boolean;
  mostrarAulas?: boolean;
  bloqueoActivo?: boolean;
  bloqueoMensaje?: string;
  bloqueoSubmensaje?: string;
  debeAlertarSuperposicion?: boolean;
  debeFiltrarCursosConCupoNoAsignado?: boolean;
  debeConsultarCupos?: boolean;
}): void {
  if (!fields.token || fields.id <= 0 || !fields.email) return;

  saveSessionSnapshot({
    token: fields.token,
    id: fields.id,
    email: fields.email,
    completeName: fields.completeName ?? { name: "", lastName: "" },
    studentInitState: fields.studentInitState,
    listadoCarreras: (fields.listadoCarreras || []) as CarreraItem[],
    carreraActual: fields.carreraActual ?? "",
    mostrarHorarios: fields.mostrarHorarios ?? true,
    mostrarAulas: fields.mostrarAulas ?? true,
    bloqueoActivo: !!fields.bloqueoActivo,
    bloqueoMensaje: fields.bloqueoMensaje ?? "",
    bloqueoSubmensaje: fields.bloqueoSubmensaje ?? "",
    debeAlertarSuperposicion: !!fields.debeAlertarSuperposicion,
    debeFiltrarCursosConCupoNoAsignado: !!fields.debeFiltrarCursosConCupoNoAsignado,
    debeConsultarCupos: !!fields.debeConsultarCupos,
  });
}
